import { FC } from "react";
import { Grid, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import StyledTextField from "components/StyledTextField";
import { ChecklistQuestionType, ChecklistType, QuestionDataType } from "operations/schema/schema";
import { ConditionalGroupType, GroupType } from "components/job/checklist/types";
import { DataTypeComponents } from "components/job/checklist/dataTypeComponents";

interface ChecklistRendererProps {
  checklist: ChecklistType;
  setChecklist: (updatedChecklist: ChecklistType | null) => void;
  editable?: boolean;
  showSaveButton?: boolean;
  onSave?: () => void;
  renderSaveButton?: JSX.Element;
}

export const ChecklistRenderer: FC<ChecklistRendererProps> = ({
  checklist,
  setChecklist,
  editable = true,
  showSaveButton = true,
  onSave,
  renderSaveButton,
}) => {
  const intl = useIntl();

  const updateQuestion = (updatedQuestion: ChecklistQuestionType) => {
    const updatedQuestions = checklist.questions.map((q) =>
      q.nodeKey === updatedQuestion.nodeKey ? updatedQuestion : q
    );
    setChecklist({ ...checklist, isTemplate: false, questions: updatedQuestions });
  };

  const handleOnChange = (
    nodeKey: string | null | undefined,
    value: any,
    property: "comment" | "answer"
  ) => {
    const updatedQuestions = checklist.questions.map((q) =>
      q.nodeKey === nodeKey ? { ...q, [property]: value } : q
    );
    setChecklist({ ...checklist, isTemplate: false, questions: updatedQuestions });
  };

  const getQuestionAnswerType = (question: ChecklistQuestionType) => {
    const Component = DataTypeComponents[question.dataType];
    if (!Component) return null;

    if (question.dataType === QuestionDataType.Expression) {
      if (!question.expression) return null;

      return (
        <Component
          question={question}
          onChange={updateQuestion}
          isInt={false}
          data-testid={`ChecklistDialog-Question-${question.nodeKey}`}
        />
      );
    }

    return (
      <Component
        question={question}
        onChange={(value) => handleOnChange(question.nodeKey, value, "answer")}
        isInt={question.dataType === QuestionDataType.Int32}
        readonly={!editable}
        data-testid={`ChecklistDialog-Question-${question.nodeKey}`}
      />
    );
  };

  const renderQuestion = (question: ChecklistQuestionType): JSX.Element => {
    const groupQuestions = checklist.questions.filter(
      (q) => q.parent === question.nodeKey && q.nodeKey !== question.nodeKey
    );

    if (question.dataType === QuestionDataType.ConditionalGroup) {
      return (
        <ConditionalGroupType
          key={question.nodeKey}
          question={question}
          onChange={(value) => handleOnChange(question.nodeKey, value, "answer")}
          data-testid={`ChecklistDialog-GroupType-${question.nodeKey}`}
        >
          {groupQuestions.map((q) => renderQuestion(q))}
        </ConditionalGroupType>
      );
    }

    if (question.dataType === QuestionDataType.Group) {
      return (
        <GroupType key={question.nodeKey} question={question}>
          {groupQuestions.map((q) => renderQuestion(q))}
        </GroupType>
      );
    }

    return (
      <Grid
        key={question.nodeKey}
        item
        container
        direction="column"
        width="100%"
        marginTop="5px"
        marginBottom="5px"
      >
        <Grid item>
          <Typography>{question.question}</Typography>
        </Grid>
        <Grid item width="100%">
          {getQuestionAnswerType(question)}
        </Grid>
        {question.showComment && (
          <Grid item mt={1} mb={1}>
            <StyledTextField
              value={question.comment}
              label={intl.formatMessage({ id: "general.comment" })}
              onChange={(event) =>
                handleOnChange(question.nodeKey, event.target.value, "comment")
              }
              size="small"
              data-testid={`ChecklistDialog-Comment-${question.nodeKey}`}
            />
          </Grid>
        )}
      </Grid>
    );
  };

  const rootQuestions = checklist.questions.filter(
    (q) => !q.parent || q.parent === q.nodeKey
  );

  return (
    <Grid container direction="column" spacing={1}>
      {rootQuestions.map((q) => renderQuestion(q))}
      {showSaveButton && onSave && (
        <Grid item mt={2}>
          {renderSaveButton ?? (
            <button onClick={onSave}>
              <FormattedMessage id="general.save" />
            </button>
          )}
        </Grid>
      )}
    </Grid>
  );
};